<template>
  <div class="container">
    <div class="chat-container">
      <ScreenTutorial v-if="showTutorial" @close="handleClose" />
      <ScreenContent />
    </div>
  </div>
</template>

<script setup lang="ts">
import { cloneDeep, isEmpty } from 'lodash';

import ScreenContent from '@/components/domains/chat/chat-screen/ScreenContent.vue';
import ScreenTutorial from '@/components/domains/chat/chat-screen/ScreenTutorial.vue';
import { useHangUpCommon } from '@/composables/domains/chat/useHangupCommon';
import { useSocketScreen } from '@/composables/domains/chat/useSocketScreen';
import { useChatPreferences } from '@/composables/shared/useChatPreferences';
import { useDeviceSize } from '@/composables/shared/useDeviceSize';
import useHotkey from '@/composables/shared/useHotkey';
import { useCallStore } from '@/stores/call';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast';
import { SocketState } from '@/utils/enums';
import {
  ij_bottomBarStatus,
  ij_chatId,
  ij_isEndedCall,
  ij_isVisibleButtonMyLanguage,
  ij_setBottomBarStatus,
} from '@/utils/injectionKeys';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const socketStore = useSocketStore();
const callStore = useCallStore();
const toastStore = useToastStore();

const { isMobileOrTablet } = useDeviceSize();
const { updateScreenPreferences } = useChatPreferences();

const { socketState } = storeToRefs(socketStore);

const retryCount = ref<number>(0);
const waitingToRetry = ref<boolean>(false);

const chatId = ref('');
const isVisibleButtonMyLanguage = ref(true); // 나의 언어만 보기 버튼 보임 여부
const isEndedCall = ref(false); // 수동적으로 통역가 종료되었는지 여부
const bottomBarStatus = ref(1); // -2:recover / -1:failed / 0:on / 1:connecting / 2:end
const showTutorial = ref(false);

let intervalReconnect = 0;

const { connectToWebsocket } = useSocketScreen(chatId);

const setBottomBarStatus = (newVal: number) => {
  bottomBarStatus.value = newVal;
};
useHotkey();

const { handleHangUp } = useHangUpCommon({ setBottomBarStatus });

provide(ij_chatId, chatId);
provide(ij_isVisibleButtonMyLanguage, isVisibleButtonMyLanguage);
provide(ij_isEndedCall, isEndedCall);
provide(ij_bottomBarStatus, bottomBarStatus);
provide(ij_setBottomBarStatus, setBottomBarStatus);

const applySettingsQuery = () => {
  const settingsQuery = cloneDeep(route.query);
  delete settingsQuery.code;
  if (isEmpty(settingsQuery)) return;

  const settingsNumeric = ['screenScrollHeight', 'screenContainerHeight'];
  const settingsPixelToRem = ['screenContainerHeight'];
  Object.entries(settingsQuery).forEach(([key, value]) => {
    if (settingsNumeric.includes(key)) {
      const newVal = parseFloat(value as string);
      settingsQuery[key] = newVal;
      if (settingsPixelToRem.includes(key)) settingsQuery[key] = newVal / 16;
    }
  });
  updateScreenPreferences(settingsQuery);
};

const reconnectAfterDelay = () => {
  setTimeout(async () => {
    if (bottomBarStatus.value === 1) {
      await connectToWebsocket();
      retryCount.value += 1;
      waitingToRetry.value = false;
    }
  }, 3000);
};

const handleRetryOrExit = () => {
  if (!waitingToRetry.value) {
    waitingToRetry.value = true;
    setBottomBarStatus(1);
    reconnectAfterDelay();
  }
};

const handleClose = () => {
  showTutorial.value = false;
};

onBeforeMount(async () => {
  await connectToWebsocket();
  if (!callStore.qrCodeInfo.options?.screenMode) {
    await router.push({ name: 'error-not-found' });
  }
});

onMounted(() => {
  let isMounted = false;

  applySettingsQuery();

  if (isMobileOrTablet.value) {
    document.addEventListener('visibilitychange', async () => {
      if (!isMounted) return;

      if (document.visibilityState === 'visible' && socketStore.socketState === SocketState.CLOSED) {
        toastStore.info(t('try_reconnection'), 'RECONNECT');
        handleRetryOrExit();
      }
    });
  }

  isMounted = true;
});

// 강퇴 여부
watch(
  () => socketStore.evicted,
  (newVal) => {
    if (newVal) handleHangUp();
  }
);

// 소켓 연결 관리
watch(
  () => socketStore.socketState,
  async (state) => {
    // 데모 종료 / 퇴장 처리 / 복구 상태일 경우 무시
    if (socketStore.endOfDemo || socketStore.evicted || socketStore.recover) {
      return;
    }

    switch (state) {
      case SocketState.OPEN: // 소켓 연결 성공
        setTimeout(() => {
          if (socketStore.socketState === SocketState.OPEN) {
            setTimeout(() => {
              socketStore.sendMessageAttendees();
            }, 3000);

            retryCount.value = 0;

            if (bottomBarStatus.value !== -2) {
              setBottomBarStatus(0);
            }
          }
        }, 500);
        break;

      case SocketState.RETRY_FAILED: // 재시도 실패
        setBottomBarStatus(-1);
        socketStore.removeAttendeeAllExcludeMine();
        break;

      case SocketState.CLOSED: // 소켓 닫힘
        if (bottomBarStatus.value !== 2) {
          handleRetryOrExit();
        }
        socketStore.removeAttendeeAllExcludeMine();
        break;

      case SocketState.ERROR_NO_HEADER: // 헤더 없는 에러
        if (socketStore.socket?.readyState !== WebSocket.OPEN) {
          await connectToWebsocket();
        }
        socketStore.sendMessageAudioStreamStart();
        break;
    }
  }
);

// 소켓 연결 상태 감시
watch(socketState, (newVal, oldVal) => {
  switch (newVal) {
    case SocketState.OPEN:
      clearInterval(intervalReconnect);
      intervalReconnect = 0;
      setBottomBarStatus(0);
      break;

    case SocketState.CLOSED:
      // 고의로 연결을 끊은 것이 아닐 경우
      if (bottomBarStatus.value === 1) {
        clearInterval(intervalReconnect);

        intervalReconnect = setInterval(async () => {
          await connectToWebsocket();
        }, 5000);
      }
      break;
  }
});
</script>

<style lang="scss" scoped>
.container {
  width: 100%;
  height: 100dvh;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  overflow: hidden;
}
</style>
